import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Spin } from 'antd';
import {
  ShopOutlined,
  MailOutlined,
  NumberOutlined,
  HomeOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import {
  createBranch,
  refreshStoredBranches,
  type BranchResponse,
  type CreateBranchPayload,
} from './addBranchApi';

interface AddBranchValues {
  branchName: string;
  branchCode: string;
  branchAddress: string;
  email?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
}

// API field name -> form field name
const FIELD_MAP: Record<string, keyof AddBranchValues> = {
  name: 'branchName',
  code: 'branchCode',
  branchCode: 'branchCode',
  address: 'branchAddress',
  email: 'email',
  phoneNumber1: 'phoneNumber1',
  phoneNumber2: 'phoneNumber2',
};

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const CODE_EXISTS_MESSAGE = 'Branch code already exists';

interface AddBranchProps {
  // called after the branch is created
  onAdded: (branch?: BranchResponse) => void;
  // called when the user cancels; omit to hide the Cancel button (first-branch onboarding)
  onCancel?: () => void;
  submitLabel?: string;
  // text under the full-screen loader while saving
  loadingTip?: string;
}

export default function AddBranchIndex({
  onAdded,
  onCancel,
  submitLabel = 'Add branch',
  loadingTip,
}: AddBranchProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<AddBranchValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The submit button stays disabled until every required field is filled and valid
  const watched = Form.useWatch([], form);
  const [isFormValid, setIsFormValid] = useState(false);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        setIsFormValid(true);
      })
      .catch(() => {
        setIsFormValid(false);
      });
  }, [form, watched]);

  const showFieldError = (field: string, message: string) => {
    const name = FIELD_MAP[field];
    if (name === undefined) return false;
    form.setFields([{ name, errors: [message] }]);
    return true;
  };

  const onFinish = async (values: AddBranchValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload: CreateBranchPayload = {
        name: values.branchName.trim(),
        code: values.branchCode.trim(),
        address: values.branchAddress.trim(),
      };
      const email = values.email?.trim() ?? '';
      if (email !== '') payload.email = email;
      const phone1 = values.phoneNumber1?.trim() ?? '';
      const phone2 = values.phoneNumber2?.trim() ?? '';
      if (phone1 !== '') payload.phoneNumber1 = phone1;
      if (phone2 !== '') payload.phoneNumber2 = phone2;

      const res = await createBranch(payload);
      dispatch(
        showSuccessMessage(res?.message ?? 'New Branch created successfully')
      );
      form.resetFields();
      try {
        await refreshStoredBranches();
      } catch {
        // The refresh failed: keep at least the new branch so the app can open
        if (res?.branch !== undefined) {
          window.localStorage.setItem(
            'branches',
            JSON.stringify([{ ...res.branch, id: res.branch._id }])
          );
        }
      }
      onAdded(res?.branch as BranchResponse | undefined);
    } catch (error: any) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      const message: string =
        typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;
      const keys: string[] | undefined = data?.validation?.body?.keys;

      if (status === 401) {
        window.localStorage.clear();
        navigate('/sign-in');
      } else if (status === 403) {
        dispatch(showErrorMessage(message));
      } else if (Array.isArray(keys) && keys.length > 0) {
        const validationMessage: string =
          data?.validation?.body?.message ?? 'Invalid value';
        const shown = keys.filter((key) =>
          showFieldError(key, validationMessage)
        );
        if (shown.length === 0) dispatch(showErrorMessage(validationMessage));
      } else if (data?.field === 'code' || data?.field === 'branchCode') {
        // 400 "Branch code is already in use"
        showFieldError('branchCode', message);
      } else if (
        typeof data?.field === 'string' &&
        showFieldError(String(data.field), message)
      ) {
        // any other field error from the server, e.g. field "email":
        // "Unable to add this email. Please use a different email" (the address belongs to another organization)
      } else if (message === 'Branch already exists') {
        // Today the backend sends this as a 500 when the code exists in the organization
        showFieldError('branchCode', CODE_EXISTS_MESSAGE);
      } else {
        dispatch(showErrorMessage(status === 500 ? GENERIC_MESSAGE : message));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Spin spinning={isSubmitting} fullscreen tip={loadingTip} />
      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && <span className="settings__req">*</span>}
          </>
        )}
        onFinish={onFinish}
      >
        <div className="settings__grid">
          <Form.Item
            label="Branch name"
            name="branchName"
            rules={[
              { required: true, message: 'Please enter branch name' },
              { whitespace: true, message: 'Please enter branch name' },
            ]}
          >
            <Input
              className="settings__input"
              placeholder="e.g. Karur"
              prefix={<ShopOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Branch code"
            name="branchCode"
            normalize={(value: string) =>
              (value ?? '').replace(/[^a-zA-Z]/g, '').toUpperCase()
            }
            rules={[
              { required: true, message: 'Please enter branch code' },
              {
                pattern: /^[A-Z]{3}$/,
                message: 'Branch code must be 3 letters (e.g. KRR)',
              },
            ]}
          >
            <Input
              className="settings__input"
              placeholder="e.g. KRR"
              maxLength={3}
              prefix={<NumberOutlined />}
            />
          </Form.Item>

          <Form.Item
            className="full"
            label="Branch address"
            name="branchAddress"
            rules={[
              { required: true, message: 'Please enter branch address' },
              { whitespace: true, message: 'Please enter branch address' },
            ]}
          >
            <Input
              className="settings__input"
              placeholder="Door no., street, city, state, PIN code"
              prefix={<HomeOutlined />}
            />
          </Form.Item>

          <Form.Item
            className="full"
            label="Email"
            name="email"
            rules={[{ type: 'email', message: 'Enter a valid email address' }]}
          >
            <Input
              className="settings__input"
              placeholder="branch@company.com"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Phone number 1"
            name="phoneNumber1"
            rules={[
              {
                pattern: /^[6-9]\d{9}$/,
                message: 'Enter a valid 10-digit mobile number',
              },
            ]}
          >
            <Input
              className="settings__input"
              placeholder="98765 43210"
              maxLength={10}
              prefix={<PhoneOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Phone number 2"
            name="phoneNumber2"
            rules={[
              {
                pattern: /^[6-9]\d{9}$/,
                message: 'Enter a valid 10-digit mobile number',
              },
            ]}
          >
            <Input
              className="settings__input"
              placeholder="98765 43210"
              maxLength={10}
              prefix={<PhoneOutlined />}
            />
          </Form.Item>
        </div>

        <div className="settings__actions">
          {onCancel !== undefined && (
            <Button
              onClick={onCancel}
              disabled={isSubmitting}
              className="settings__button settings__button--ghost"
            >
              Cancel
            </Button>
          )}
          <Button
            htmlType="submit"
            disabled={!isFormValid || isSubmitting}
            className="settings__button settings__button--primary"
          >
            {submitLabel}
          </Button>
        </div>
      </Form>
    </div>
  );
}
